import { t } from "i18next";

import { errors } from "../constants";
import { createErrorResult, SimpleResult } from "../dto";
import { Api, getCompanyId, Log } from "../helpers";
import {
  ICheckStationRouteExistsPayload,
  ICheckStationRoutePausedPayload,
  IConvertStationRouteToCustomizedPayload,
  IConvertStationRouteToCustomizedResponse,
  IDeleteStationRoutePayload,
  IMatchingMlPathsPayload,
  IMatchingMlPathsResponse,
  IRideSaveRoutePayload,
  IRideSaveRouteResponse,
  IStationRouteUsageStatsDto,
  RecountStationRouteDistancesCriteriaDto,
  RecountStationsRouteResultDto,
} from "../models";

export class StationRouteApi {
  static async recountStationInfo(
    criteria: RecountStationRouteDistancesCriteriaDto,
  ): Promise<SimpleResult<RecountStationsRouteResultDto> | undefined> {
    const { stations } = criteria;

    if (!stations?.length) {
      Log.warnAndToast(t("errors.invalidDataForRecountNoStationOfDate"));
      return undefined;
    }

    const response = await Api.post<
      SimpleResult<RecountStationsRouteResultDto>
    >(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/recount-stations`,
      criteria,
    );

    return response?.data;
  }

  static async saveStationRoute(
    payload: IRideSaveRoutePayload,
  ): Promise<SimpleResult<IRideSaveRouteResponse> | undefined> {
    const { route } = payload;

    if (!route.orderedStationIds?.length) {
      return createErrorResult(errors.stationRoute.noStationsInRoute);
    }

    const response = await Api.post<
      SimpleResult<IRideSaveRouteResponse> | undefined
    >(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/save-station-route`,
      payload,
    );

    return response?.data;
  }

  static async convertToCustomized(
    payload: IConvertStationRouteToCustomizedPayload,
  ): Promise<
    SimpleResult<IConvertStationRouteToCustomizedResponse> | undefined
  > {
    const { stationRouteId } = payload;

    if (!stationRouteId) {
      return createErrorResult(errors.common.invalidId);
    }

    const response = await Api.post<
      SimpleResult<IConvertStationRouteToCustomizedResponse> | undefined
    >(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/convert-to-customized`,
      payload,
    );

    return response?.data;
  }

  static async deleteStationRoute(
    payload: IDeleteStationRoutePayload,
  ): Promise<SimpleResult<void> | undefined> {
    const { stationRouteId } = payload;

    if (!stationRouteId) {
      return createErrorResult(errors.stationRoute.noRouteToDelete);
    }

    const response = await Api.post<SimpleResult<void> | undefined>(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/delete-station-route`,
      payload,
    );

    return response?.data;
  }

  static async checkStationRouteExists(
    payload: ICheckStationRouteExistsPayload,
  ): Promise<SimpleResult<boolean> | undefined> {
    const { stationRouteId } = payload;

    if (!stationRouteId) {
      return createErrorResult(errors.common.invalidId);
    }

    const response = await Api.post<SimpleResult<boolean> | undefined>(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/station-route-exists`,
      payload,
    );

    return response?.data;
  }

  static async checkStationRoutePaused(
    payload: ICheckStationRoutePausedPayload,
  ): Promise<SimpleResult<boolean> | undefined> {
    const { stationRouteId } = payload;

    if (!stationRouteId) {
      return createErrorResult(errors.common.invalidId);
    }

    const response = await Api.post<SimpleResult<boolean> | undefined>(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/station-route-paused`,
      payload,
    );

    return response?.data;
  }

  static async findMatchingMlPaths(
    payload: IMatchingMlPathsPayload,
  ): Promise<SimpleResult<IMatchingMlPathsResponse> | undefined> {
    if (!payload.stationIds?.length) {
      return createErrorResult(errors.stationRoute.noStationsInRoute);
    }

    const response = await Api.post<
      SimpleResult<IMatchingMlPathsResponse> | undefined
    >(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/matching-ml-paths`,
      payload,
    );

    return response?.data;
  }

  static async getUsageStats(
    stationRouteId: number,
  ): Promise<IStationRouteUsageStatsDto | undefined> {
    if (!stationRouteId) {
      return undefined;
    }

    const response = await Api.get<IStationRouteUsageStatsDto>(
      `/api/v1/core/c/${getCompanyId()}/stationRoutes/${stationRouteId}/usage-stats`,
    );

    const usageStats: IStationRouteUsageStatsDto | undefined = response?.data;
    return usageStats;
  }
}
